import React, { useEffect, useRef, useState } from 'react';
import { getNativeApps, refreshNativeApps, selectNativeApp } from '../Apps/assignedAppApi';
import { assignedAppMessage } from '../Apps/assignedAppPresentation';

export default function LgDeviceAppEditor({ device, onClose }) {
  const [data, setData] = useState(null);
  const [choices, setChoices] = useState({});
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const mounted = useRef(false);
  const inFlight = useRef(true);
  useEffect(() => {
    mounted.current = true;
    getNativeApps(device.id).then((result) => {
      if (mounted.current) setData(result);
    }).catch((failure) => {
      if (mounted.current) setError(assignedAppMessage(failure));
    }).finally(() => {
      if (mounted.current) { inFlight.current = false; setBusy(false); }
    });
    return () => { mounted.current = false; };
  }, [device.id]);

  const profiles = data?.profiles || [];
  const report = data?.inventory?.launchable;
  const apps = (report?.apps || []).filter((app) => app.launchable !== false && app.hidden !== true);
  const profileFor = (id) => profiles.find((profile) => profile.profile.nativeApplication.nativeId === id);
  const checked = (id) => choices[id] ?? profileFor(id)?.enabled ?? false;
  // Keep missing saved choices visible so an incomplete scan never silently deselects them.
  const missing = profiles.filter((profile) => !apps.some((app) => app.nativeId === profile.profile.nativeApplication.nativeId)
    && (profile.enabled || choices[profile.profile.nativeApplication.nativeId] !== undefined))
    .map((profile) => ({ nativeId: profile.profile.nativeApplication.nativeId, name: profile.displayName, missing: true }));
  const missingChoices = Object.keys(choices).filter((id) => choices[id] && !apps.some((app) => app.nativeId === id)
    && !missing.some((app) => app.nativeId === id)).map((id) => ({ nativeId: id, name: id, missing: true }));
  const entries = [...apps, ...missing, ...missingChoices].sort((a, b) => (a.name || a.nativeId).localeCompare(b.name || b.nativeId));
  const changed = entries.filter((app) => Boolean(checked(app.nativeId)) !== Boolean(profileFor(app.nativeId)?.enabled));
  const selectedCount = entries.filter((app) => checked(app.nativeId)).length;
  const run = async (work) => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    try { await work(); }
    catch (failure) { if (mounted.current) setError(assignedAppMessage(failure)); }
    finally { if (mounted.current) { inFlight.current = false; setBusy(false); } }
  };
  const scan = () => run(async () => {
    try {
      const result = await refreshNativeApps(device.id);
      if (mounted.current) setData(result);
    } catch (failure) {
      // A failed scan can still update inventory freshness; retain unsaved ticks.
      try { const retained = await getNativeApps(device.id); if (mounted.current) setData(retained); } catch (_) { /* Keep the scan error. */ }
      throw failure;
    }
  });
  const save = () => run(async () => {
    try {
      for (const app of changed) {
        if (!mounted.current) return;
        const profile = profileFor(app.nativeId);
        const result = await selectNativeApp(device.id, {
          nativeId: app.nativeId, enabled: Boolean(checked(app.nativeId)),
          serviceKey: profile?.profile.nativeApplication.serviceKey || '',
          ...(profile ? { profileId: profile.id, revision: profile.revision } : {}),
        });
        if (!mounted.current) return;
        setData((previous) => ({ ...previous, profiles: [...(previous.profiles || []).filter((item) => item.id !== result.profile.id), result.profile] }));
        setChoices((previous) => { const next = { ...previous }; delete next[app.nativeId]; return next; });
      }
      if (mounted.current) onClose();
    } catch (failure) {
      // Individual saves can succeed before a later failure. Read back revisions before retry;
      // never replay a failed save automatically or discard the remaining local choices.
      try { const retained = await getNativeApps(device.id); if (mounted.current) setData(retained); } catch (_) { /* Keep the save error. */ }
      if (mounted.current) setError(`${assignedAppMessage(failure)} Some selections may already be saved. Review the list before retrying.`);
    }
  });

  return <div className="modal lg-device-app-editor" role="dialog" aria-modal="true" aria-labelledby="lg-device-edit-title">
    <div className="modal-content settings-modal-content settings-modal-content--wide">
      <h3 id="lg-device-edit-title">Edit Device</h3>
      <p className="settings-modal-muted">{device.name || 'LG TV'} – LG webOS</p>
      <div className="settings-modal-section">
        <strong>Other Apps (launchable)</strong>
        <p className="settings-modal-muted">Tick the apps you want to use, then save your selection.</p>
        {report?.collectedAt && <p className="settings-modal-muted">Last scan: {new Date(report.collectedAt).toLocaleString()}</p>}
        {report?.status === 'partial' && <p role="status">The TV returned a partial app list. Saved selections are retained.</p>}
        {report?.status === 'unavailable' && <p role="status">The last scan failed. Showing previously reported apps and saved selections.</p>}
        {busy && <p role="status" className="settings-modal-muted">{data ? 'Updating apps…' : 'Loading saved apps…'}</p>}
        {!busy && data && !entries.length && <p>No launchable apps reported yet. Select Refresh Apps From TV to scan.</p>}
        <label className="settings-app-search">Search apps
          <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="App name or ID" />
        </label>
        {changed.some((app) => app.missing && checked(app.nativeId)) && <p role="status">A selected app is no longer reported. Scan again or untick it before saving.</p>}
        <p className="settings-modal-muted">{selectedCount} selected{changed.length ? ` · ${changed.length} unsaved changes` : ''}</p>
        <div className="settings-modal-scroll settings-modal-scroll--compact">
          {entries.filter((app) => `${app.name} ${app.nativeId}`.toLowerCase().includes(search.toLowerCase())).map((app) => <label key={app.nativeId} className="settings-app-row">
            <input type="checkbox" checked={Boolean(checked(app.nativeId))} disabled={busy || (app.missing && !checked(app.nativeId))}
              onChange={(event) => setChoices((previous) => ({ ...previous, [app.nativeId]: event.target.checked }))} />
            <span className="settings-app-label">{app.name || app.nativeId}{app.missing && <small className="settings-app-missing">Not in the latest app list</small>}</span>
            <span className="settings-app-pkg">{app.nativeId}</span>
          </label>)}
        </div>
      </div>
      {error && <p role="alert" className="settings-modal-error">{error}</p>}
      <div className="modal-footer">
        <button type="button" className="settings-modal-button secondary" onClick={scan} disabled={busy}>Refresh Apps From TV</button>
        {error && <button type="button" className="settings-modal-button secondary" onClick={() => run(async () => {
          const result = await getNativeApps(device.id); if (mounted.current) setData(result);
        })} disabled={busy}>Reload Saved Apps</button>}
        <button type="button" className="settings-modal-button secondary" onClick={onClose} disabled={busy && Boolean(data)}>Cancel</button>
        <button type="button" className="settings-modal-button primary" onClick={save} disabled={busy || !data || !changed.length || changed.some((app) => app.missing && checked(app.nativeId))}>Save Selection</button>
      </div>
    </div>
  </div>;
}
